import * as THREE from 'three';
import { GLTFLoader, type GLTFLoaderPlugin } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

export const name = 'ttGlbLoader';

export const BASE_ASSET_URL = import.meta.env.DEV
    ? '/remote-assets'
    : 'https://tunneltwin.nbg1.your-objectstorage.com';

export interface GlbLoadOptions {
    projectName: string;
    url: string;
    transformation?: number[];
    applyLegacyProjectTransform?: boolean;
    convertGltfYUpToZUp?: boolean;
    /** Embedded photogrammetry textures are often far larger than a browser can upload safely. */
    maxTextureSize?: number;
    /** Maximum decoded texel count, independent of the GPU's per-axis limit. */
    maxTexturePixels?: number;
    textureCompression: boolean;
    /** Remove atlas space that is not referenced by any mesh UV before resizing. */
    cropUnusedTextureSpace?: boolean;
    /** Improve oblique/minified texture sampling. Supply the renderer's supported anisotropy. */
    textureAnisotropy?: number;
    signal?: AbortSignal;
}

interface ImageDimensions {
    width: number;
    height: number;
}

interface TextureCrop {
    minU: number;
    minV: number;
    maxU: number;
    maxV: number;
}

interface TextureCropPlan extends TextureCrop {
    applied?: TextureCrop;
}

interface GltfTextureInfo {
    index: number;
    texCoord?: number;
    extensions?: Record<string, unknown>;
}

const maxConcurrentTextureDecodes = 1;
const imageHeaderProbeBytes = 1024 * 1024;
const textureCropPadding = 0.002;
const minimumCropAreaSaving = 0.05;

class AsyncSemaphore {
    private active = 0;
    private readonly waiters: Array<() => void> = [];

    constructor(private readonly limit: number) {}

    acquire(signal?: AbortSignal): Promise<() => void> {
        return new Promise((resolve, reject) => {
            let queued = false;
            const abort = () => {
                if (queued) {
                    const index = this.waiters.indexOf(grant);
                    if (index >= 0) this.waiters.splice(index, 1);
                }
                reject(new DOMException('Texture decode cancelled.', 'AbortError'));
            };
            const grant = () => {
                if (signal?.aborted) {
                    abort();
                    return;
                }
                queued = false;
                signal?.removeEventListener('abort', abort);
                this.active++;
                let released = false;
                resolve(() => {
                    if (released) return;
                    released = true;
                    this.active--;
                    this.waiters.shift()?.();
                });
            };

            if (signal?.aborted) {
                abort();
            } else if (this.active < this.limit) {
                grant();
            } else {
                queued = true;
                this.waiters.push(grant);
                signal?.addEventListener('abort', abort, { once: true });
            }
        });
    }
}

const textureDecodeSemaphore = new AsyncSemaphore(maxConcurrentTextureDecodes);

function errorMessage(error: unknown): string {
    if (error instanceof Error) return `${error.name}: ${error.message}`;
    return String(error);
}

function contextualError(message: string, cause: unknown): Error {
    const error = new Error(`${message}: ${errorMessage(cause)}`);
    (error as Error & { cause?: unknown }).cause = cause;
    return error;
}

function readImageDimensions(bytes: Uint8Array): ImageDimensions | null {
    // PNG stores its dimensions in the fixed IHDR header.
    if (bytes.length >= 24
        && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        return { width: view.getUint32(16), height: view.getUint32(20) };
    }

    // JPEG dimensions live in one of the Start Of Frame segments. Reading the
    // header avoids decoding the original 25k image just to discover its size.
    if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
        const startOfFrameMarkers = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
        let offset = 2;
        while (offset + 8 < bytes.length) {
            while (offset < bytes.length && bytes[offset] !== 0xff) offset++;
            while (offset < bytes.length && bytes[offset] === 0xff) offset++;
            if (offset >= bytes.length) break;
            const marker = bytes[offset++];
            if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) continue;
            if (offset + 1 >= bytes.length) break;
            const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
            if (segmentLength < 2 || offset + segmentLength > bytes.length) break;
            if (startOfFrameMarkers.has(marker) && segmentLength >= 7) {
                return {
                    height: (bytes[offset + 3] << 8) | bytes[offset + 4],
                    width: (bytes[offset + 5] << 8) | bytes[offset + 6],
                };
            }
            offset += segmentLength;
        }
    }
    return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function collectTextureInfos(value: unknown, propertyName = '', result: GltfTextureInfo[] = []): GltfTextureInfo[] {
    if (Array.isArray(value)) {
        value.forEach((item) => collectTextureInfos(item, propertyName, result));
        return result;
    }
    if (!isRecord(value)) return result;

    if (propertyName.endsWith('Texture') && Number.isInteger(value.index)) {
        result.push(value as unknown as GltfTextureInfo);
        return result;
    }
    for (const [key, child] of Object.entries(value)) collectTextureInfos(child, key, result);
    return result;
}

/**
 * Metashape often exports a large rectangular atlas while a mesh references
 * only a fraction of it. Cropping that unreachable area before downsampling
 * spends the browser's texture budget on texels that can actually be shown.
 */
function buildTextureCropPlans(json: unknown): Map<number, TextureCropPlan> {
    const plans = new Map<number, TextureCropPlan>();
    if (!isRecord(json)) return plans;
    const meshes = Array.isArray(json.meshes) ? json.meshes : [];
    const materials = Array.isArray(json.materials) ? json.materials : [];
    const textures = Array.isArray(json.textures) ? json.textures : [];
    const accessors = Array.isArray(json.accessors) ? json.accessors : [];
    const invalidSources = new Set<number>();

    for (const mesh of meshes) {
        if (!isRecord(mesh) || !Array.isArray(mesh.primitives)) continue;
        for (const primitive of mesh.primitives) {
            if (!isRecord(primitive) || !Number.isInteger(primitive.material) || !isRecord(primitive.attributes)) continue;
            const material = materials[primitive.material as number];
            if (!isRecord(material)) continue;

            for (const textureInfo of collectTextureInfos(material)) {
                const texture = textures[textureInfo.index];
                if (!isRecord(texture) || !Number.isInteger(texture.source)) continue;
                const sourceIndex = texture.source as number;
                // A texture transform can rotate or shift coordinates outside
                // the accessor's raw bounds, so retain the complete image.
                if (isRecord(textureInfo.extensions?.KHR_texture_transform)) {
                    invalidSources.add(sourceIndex);
                    continue;
                }
                const texCoord = Number.isInteger(textureInfo.texCoord) ? textureInfo.texCoord! : 0;
                const accessorIndex = primitive.attributes[`TEXCOORD_${texCoord}`];
                const accessor = Number.isInteger(accessorIndex) ? accessors[accessorIndex as number] : null;
                if (!isRecord(accessor) || !Array.isArray(accessor.min) || !Array.isArray(accessor.max)
                    || accessor.min.length < 2 || accessor.max.length < 2) {
                    invalidSources.add(sourceIndex);
                    continue;
                }
                const [minU, minV] = accessor.min;
                const [maxU, maxV] = accessor.max;
                if (![minU, minV, maxU, maxV].every((item) => typeof item === 'number' && Number.isFinite(item))
                    || minU < 0 || minV < 0 || maxU > 1 || maxV > 1 || minU >= maxU || minV >= maxV) {
                    invalidSources.add(sourceIndex);
                    continue;
                }

                const previous = plans.get(sourceIndex);
                if (previous) {
                    previous.minU = Math.min(previous.minU, minU);
                    previous.minV = Math.min(previous.minV, minV);
                    previous.maxU = Math.max(previous.maxU, maxU);
                    previous.maxV = Math.max(previous.maxV, maxV);
                } else {
                    plans.set(sourceIndex, { minU, minV, maxU, maxV });
                }
            }
        }
    }

    for (const sourceIndex of invalidSources) plans.delete(sourceIndex);
    for (const [sourceIndex, plan] of plans) {
        const usedArea = (plan.maxU - plan.minU) * (plan.maxV - plan.minV);
        if (1 - usedArea < minimumCropAreaSaving) plans.delete(sourceIndex);
    }
    return plans;
}

function imageCropForPlan(plan: TextureCropPlan, dimensions: ImageDimensions): {
    left: number;
    top: number;
    width: number;
    height: number;
} {
    const minU = Math.max(0, plan.minU - textureCropPadding);
    const minV = Math.max(0, plan.minV - textureCropPadding);
    const maxU = Math.min(1, plan.maxU + textureCropPadding);
    const maxV = Math.min(1, plan.maxV + textureCropPadding);
    const left = Math.floor(minU * dimensions.width);
    const top = Math.floor(minV * dimensions.height);
    const right = Math.ceil(maxU * dimensions.width);
    const bottom = Math.ceil(maxV * dimensions.height);
    plan.applied = {
        minU: left / dimensions.width,
        minV: top / dimensions.height,
        maxU: right / dimensions.width,
        maxV: bottom / dimensions.height,
    };
    return { left, top, width: right - left, height: bottom - top };
}

function applyTextureCrop(texture: THREE.Texture, crop: TextureCrop) {
    const width = crop.maxU - crop.minU;
    const height = crop.maxV - crop.minV;
    texture.offset.set(-crop.minU / width, -crop.minV / height);
    texture.repeat.set(1 / width, 1 / height);
    texture.updateMatrix();
}

function configureTextureSampling(scene: THREE.Object3D, anisotropy: number) {
    const textures = new Set<THREE.Texture>();
    scene.traverse((child) => {
        const material = (child as THREE.Mesh).material;
        const materials = Array.isArray(material) ? material : material ? [material] : [];
        for (const item of materials) {
            for (const value of Object.values(item as unknown as Record<string, unknown>)) {
                if (value instanceof THREE.Texture) textures.add(value);
            }
        }
    });
    for (const texture of textures) {
        // Preserve deliberately nearest-filtered assets, but upgrade ordinary
        // linear glTF sampling so mipmaps and anisotropy can be used.
        if (texture.magFilter === THREE.NearestFilter || texture.minFilter === THREE.NearestFilter) continue;
        if ((texture as THREE.CompressedTexture).isCompressedTexture) {
            texture.anisotropy = Math.max(1, anisotropy);
            texture.needsUpdate = true;
            continue;
        }
        if (texture.minFilter === THREE.LinearFilter) texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.generateMipmaps = true;
        texture.anisotropy = Math.max(1, anisotropy);
        texture.needsUpdate = true;
    }
}

class ResizingImageBitmapLoader extends THREE.ImageBitmapLoader {
    constructor(
        manager: THREE.LoadingManager,
        private readonly maxTextureSize: number,
        private readonly maxTexturePixels: number | undefined,
        private readonly assetUrl: string,
        private readonly signal?: AbortSignal,
        private readonly cropPlan?: TextureCropPlan,
    ) {
        super(manager);
    }

    override load(
        url: string,
        onLoad?: (data: ImageBitmap) => void,
        _onProgress?: (event: ProgressEvent) => void,
        onError?: (err: unknown) => void,
    ): void {
        const resolvedUrl = this.manager.resolveURL(this.path ? this.path + url : url);
        this.manager.itemStart(resolvedUrl);
        const credentials = this.crossOrigin === 'anonymous' ? 'same-origin' : 'include';
        let stage = 'fetch';
        void (async () => {
            const response = await fetch(resolvedUrl, {
                credentials,
                headers: this.requestHeader,
                signal: this.signal,
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status} ${response.statusText}`);
            }

            stage = 'reading texture data';
            const blob = await response.blob();
            const releaseDecodeSlot = await textureDecodeSemaphore.acquire(this.signal);
            try {
                stage = 'reading texture dimensions';
                const header = await blob.slice(0, imageHeaderProbeBytes).arrayBuffer();
                const dimensions = readImageDimensions(new Uint8Array(header));
                if (!dimensions) {
                    throw new Error('Could not determine embedded PNG/JPEG dimensions before decoding.');
                }

                stage = 'decoding and resizing texture';
                const crop = this.cropPlan
                    ? imageCropForPlan(this.cropPlan, dimensions)
                    : { left: 0, top: 0, width: dimensions.width, height: dimensions.height };
                const bitmapOptions: ImageBitmapOptions = {
                    colorSpaceConversion: 'none',
                    premultiplyAlpha: 'none',
                };
                const dimensionScale = this.maxTextureSize / Math.max(crop.width, crop.height);
                const pixelScale = this.maxTexturePixels && this.maxTexturePixels > 0
                    ? Math.sqrt(this.maxTexturePixels / (crop.width * crop.height))
                    : 1;
                const scale = Math.min(1, dimensionScale, pixelScale);
                if (scale < 1) {
                    // Floor both axes so rounding can never exceed either GPU limit.
                    bitmapOptions.resizeWidth = Math.max(1, Math.floor(crop.width * scale));
                    bitmapOptions.resizeHeight = Math.max(1, Math.floor(crop.height * scale));
                    bitmapOptions.resizeQuality = 'high';
                }
                const bitmap = this.cropPlan
                    ? await createImageBitmap(blob, crop.left, crop.top, crop.width, crop.height, bitmapOptions)
                    : await createImageBitmap(blob, bitmapOptions);
                if (this.signal?.aborted) {
                    bitmap.close();
                    throw new DOMException('Texture decode cancelled.', 'AbortError');
                }
                if (this.cropPlan || bitmap.width !== dimensions.width || bitmap.height !== dimensions.height) {
                    console.info(
                        `[TunnelTwin] Texture ${dimensions.width}x${dimensions.height}`
                        + `${this.cropPlan ? ` cropped to ${crop.width}x${crop.height}` : ''}`
                        + ` decoded at ${bitmap.width}x${bitmap.height}.`,
                    );
                }
                onLoad?.(bitmap);
            } finally {
                releaseDecodeSlot();
            }
        })().catch((error) => {
            if ((error as DOMException)?.name === 'AbortError') {
                onError?.(error);
                return;
            }
            const detailedError = contextualError(
                `Texture ${stage} failed while loading ${this.assetUrl} (${resolvedUrl})`,
                error,
            );
            console.error(detailedError);
            this.manager.itemError(resolvedUrl);
            onError?.(detailedError);
        }).finally(() => {
            this.manager.itemEnd(resolvedUrl);
        });
    }
}

function hasNonIdentityTransform(transformation?: number[]): boolean {
    if (!transformation || transformation.length !== 16) return false;
    return !new THREE.Matrix4().fromArray(transformation).equals(new THREE.Matrix4());
}

function applyProjectTransformation(scene: THREE.Object3D, transformation?: number[]) {
    if (!transformation || transformation.length !== 16) return;
    const matrix = new THREE.Matrix4().fromArray(transformation);
    if (!matrix.equals(new THREE.Matrix4())) {
        scene.applyMatrix4(matrix);
        scene.updateMatrixWorld(true);
    }
}

function applyLegacyTransform(scene: THREE.Object3D, projectName: string) {
    switch (projectName.toLowerCase()) {
        case 'sentvid':
        case 'sentvid2':
            scene.up.set(0, 0, 1);
            scene.position.set(458453, 106550, 316);
            scene.rotateX(Math.PI / 2);
            break;
        case 'logatec':
            scene.up.set(0, 0, 1);
            scene.position.set(443250, 87400, 474);
            scene.rotateX(Math.PI / 2);
            break;
    }
    scene.updateMatrixWorld(true);
}

/** Load exactly one GLB URL. Project metadata is deliberately supplied by the caller. */
export async function loadGlbAsset(options: GlbLoadOptions): Promise<THREE.Object3D> {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');
    const loader = new GLTFLoader();
    loader.setCrossOrigin('anonymous');
    loader.setDRACOLoader(dracoLoader);
    if (options.maxTextureSize && typeof createImageBitmap !== 'undefined' && !options.url.startsWith('blob:http')) {
        loader.register((parser) => {
            const cropPlans = options.cropUnusedTextureSpace
                ? buildTextureCropPlans(parser.json)
                : new Map<number, TextureCropPlan>();
            return {
                name: 'TT_resized_embedded_textures',
                loadTexture(textureIndex) {
                    const textureDef = parser.json.textures?.[textureIndex];
                    const sourceIndex = textureDef?.source;
                    // Alternate format plugins run before this one. If they do
                    // not handle the texture, resize its standard fallback source.
                    if (!Number.isInteger(sourceIndex)) return null;
                    const cropPlan = cropPlans.get(sourceIndex);

                    if(!options.textureCompression){
                        options.maxTextureSize = 15000 * 8000;
                        options.maxTexturePixels = 15000 * 8000;
                    }

                    const textureLoader = new ResizingImageBitmapLoader(
                        parser.options.manager,
                        options.maxTextureSize!,
                        options.maxTexturePixels,
                        options.url,
                        options.signal,
                        cropPlan,
                    );
                    textureLoader.setCrossOrigin(parser.options.crossOrigin);
                    textureLoader.setRequestHeader(parser.options.requestHeader);
                    return parser.loadTextureImage(textureIndex, sourceIndex, textureLoader).then((texture) => {
                        if (texture && cropPlan?.applied) applyTextureCrop(texture, cropPlan.applied);
                        return texture;
                    });
                },
            } as GLTFLoaderPlugin;
        });
    }
    try {
        const response = await fetch(options.url, {
            credentials: 'same-origin',
            signal: options.signal,
        });
        if (!response.ok) {
            throw new Error(`GLB request failed with HTTP ${response.status} ${response.statusText}`);
        }
        const data = await response.arrayBuffer();
        if (options.signal?.aborted) {
            throw new DOMException('GLB load cancelled.', 'AbortError');
        }

        var resourcePath = options.url;
        if(!options.url.startsWith('blob:http')){
            const baseUrl = new URL(response.url || options.url, globalThis.location?.href);
            resourcePath = new URL('.', baseUrl).href;
        }
        const gltf = await loader.parseAsync(data, resourcePath);
        const scene = gltf.scene;
        if (options.convertGltfYUpToZUp) {
            scene.applyMatrix4(new THREE.Matrix4().makeRotationX(Math.PI / 2));
            scene.updateMatrixWorld(true);
        }
        if (options.applyLegacyProjectTransform && !hasNonIdentityTransform(options.transformation)) {
            applyLegacyTransform(scene, options.projectName);
        }
        applyProjectTransformation(scene, options.transformation);
        if (options.textureAnisotropy !== undefined) {
            configureTextureSampling(scene, options.textureAnisotropy);
        }
        return scene;
    } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') throw error;
        const detailedError = contextualError(`Failed to load GLB ${options.url}`, error);
        console.error(detailedError);
        throw detailedError;
    } finally {
        dracoLoader.dispose();
    }
}

/** Legacy compatibility wrapper retained for external call sites. */
export async function ttGlbLoader(projectName: string, modelName: string): Promise<THREE.Object3D[]> {
    const url = `${BASE_ASSET_URL}/models/${encodeURIComponent(projectName)}/models/${encodeURIComponent(modelName)}`;
    return [await loadGlbAsset({ projectName, url, applyLegacyProjectTransform: true })];
}
